import type { ChatMessage } from "./protocol";

/**
 * The oldest message the user hasn't read — where a chat should open, rather than at the newest, which
 * has already scrolled past whatever arrived while they were gone.
 *
 * The test is the receipt, not a local watermark, because receipts are the cross-device signal: something
 * read on the phone must not re-anchor here. System markers are silent and never count. Kept identical to
 * the phone's predicate so the two surfaces can't disagree about where you left off.
 */
export function firstUnreadId(messages: ChatMessage[]): string | null {
  const m = messages.find(
    (x) => x.role === "agent" && !x.sys && !x.peer && x.receipt !== "read" && x.receipt !== "ack" && !!x.id,
  );
  return m ? m.id : null;
}

/** How many unread messages a chat has, for the sidebar — same predicate, so a badge can't disagree
 *  with where the chat actually opens. */
export function unreadCount(messages: ChatMessage[]): number {
  return messages.filter(
    (x) => x.role === "agent" && !x.sys && !x.peer && x.receipt !== "read" && x.receipt !== "ack",
  ).length;
}

/**
 * How long to let a message go unread before saying so.
 *
 * The server retries a wake that went missing for about four and a half minutes before it gives up. Saying
 * anything inside that window would be reporting the retries as a problem while they are still running; past
 * it, the retries are finished and the message really is sitting there.
 */
export const UNREAD_NOTICE_AFTER_MS = 5 * 60_000;

/**
 * How long the oldest thing the user said has been waiting to be read, or null when nothing is waiting.
 *
 * The other direction from the functions above: those are agent messages this reader owes a receipt for,
 * this is the reader's own messages that nobody has picked up. A message counts as waiting until the agent
 * stamps it read or acknowledged — everything before that is the same from the sender's side.
 *
 * Measured from the OLDEST one, because that is how long the conversation has been stalled; the newest would
 * restart the clock every time the user, getting no answer, says something else.
 */
export function unreadWaitMs(messages: ChatMessage[], now: number): number | null {
  const waiting = messages.filter(
    (x) => x.role === "user" && !x.sys && x.receipt !== "read" && x.receipt !== "ack",
  );
  if (waiting.length === 0) return null;
  const oldest = Math.min(...waiting.map((x) => x.ts));
  // A message stamped from a clock running ahead of this one would otherwise read as waiting a negative
  // time, which passes any "long enough" test written as a comparison.
  return Math.max(0, now - oldest);
}

/** A wait as a length of time, coarse enough that it doesn't redraw every time it is looked at. */
export function describeWait(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

/**
 * The header line for a chat the agent has not read, or null while there is nothing to say.
 *
 * This outranks everything else the header could show. "offline", "idle" and a live activity line all
 * describe the agent; none of them tells the user the thing they actually want to know, which is whether
 * what they said got through. A session can read as active and still be sitting on a message.
 */
export function unreadNotice(messages: ChatMessage[], now: number): string | null {
  const waited = unreadWaitMs(messages, now);
  if (waited === null || waited < UNREAD_NOTICE_AFTER_MS) return null;
  return `not read for ${describeWait(waited)}`;
}
